import React from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Sparkles,
  Compass,
  Syringe,
  Video,
  Stethoscope,
  Users,
} from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const BOOKING_URL = "/book";

const SERVICE_META = [
  { icon: Sparkles, key: "ivf", price: "$1,250", span: "md:col-span-7", tone: "bg-[#3F5043] text-[#F8F7F3]", label: true },
  { icon: Compass, key: "iui", price: "$800", span: "md:col-span-5", tone: "bg-[#AE8F78] text-[#F8F7F3]" },
  { icon: Users, key: "care", price: "$500", span: "md:col-span-5", tone: "bg-[#EDEDEE] text-[#3F5043]" },
  { icon: Heart, key: "comp", price: "$95", span: "md:col-span-7", tone: "bg-[#F8F7F3] border border-[#BCB3A6]/40 text-[#3F5043]" },
  { icon: Heart, key: "new", price: "$100", span: "md:col-span-4", tone: "bg-[#EDEDEE] text-[#3F5043]" },
  { icon: Stethoscope, key: "prep", price: "$95", span: "md:col-span-4", tone: "bg-[#EDEDEE] text-[#3F5043]" },
  { icon: Syringe, key: "med", price: "$80", span: "md:col-span-4", tone: "bg-[#EDEDEE] text-[#3F5043]" },
  { icon: Video, key: "vmed", price: "$60", span: "md:col-span-12", tone: "bg-[#875F45] text-[#F8F7F3]" },
];

export default function Services() {
  const { t } = useT();
  const SERVICES = SERVICE_META.map((m) => ({
    ...m,
    title: t(`svc.${m.key}.title`),
    duration: t(`svc.${m.key}.duration`),
    description: t(`svc.${m.key}.desc`),
  }));
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 sm:py-32 bg-[#F8F7F3] relative"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="mb-14 sm:mb-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-4">
              {t("services.eyebrow")}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.05] text-[#3F5043]">
              {t("services.heading.a")}{" "}
              <em className="text-[#875F45]">{t("services.heading.em")}</em>{" "}
              {t("services.heading.b")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-base sm:text-lg text-[#3F5043]/75 leading-relaxed">
              {t("services.intro")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className={`relative rounded-3xl p-8 sm:p-10 ${s.span} ${s.tone} transition-transform duration-300 hover:-translate-y-1 flex flex-col`}
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon size={28} strokeWidth={1.4} className="opacity-90" />
                  {s.label && (
                    <span className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold opacity-80 bg-[#F8F7F3]/15 px-3 py-1 rounded-full">
                      {t("services.label.signature")}
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-normal leading-snug mb-2">
                  {s.title}
                </h3>
                <div className="flex items-baseline gap-3 mb-3 opacity-90">
                  <span className="font-serif text-2xl sm:text-3xl">
                    {s.price}
                  </span>
                  <span className="text-xs uppercase tracking-[0.18em] opacity-75">
                    {s.duration}
                  </span>
                </div>
                <p className="text-sm sm:text-base leading-relaxed opacity-85 mb-5">
                  {s.description}
                </p>
                <a
                  href={BOOKING_URL}
                  data-testid={`service-book-${i}`}
                  className="mt-auto inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] font-semibold opacity-90 hover:opacity-100 group/btn"
                >
                  {t("services.book")}
                  <span className="transition-transform group-hover/btn:translate-x-1">
                    →
                  </span>
                </a>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a
            href={BOOKING_URL}
            data-testid="services-book-all-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full px-9 py-4 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#899480] transition-colors duration-300"
          >
            {t("services.viewAll")}
          </a>
        </div>
      </div>
    </section>
  );
}
